import { InfoBanner } from '@ui';
import { useMemo } from 'react';
import { CaseResourceCombined, CaseResourceType, OrganType } from '@/api/apis/case';
import { TrialProgram } from '@/api/apis/facilities/trial-programs';
import { useInfiniteOcsConsoleListQuery } from '@/queries/ocs-consoles';

const SPECIFIC_OCS_DEVICE_TRIAL_BANNER_TEXT = 'Please ensure the trial specific OCS device is selected for the case.';
const OCS_DEVICE_TRIAL_BANNER_TEXT = 'Please ensure the appropriate OCS device is selected for the case.';
const REMOVE_OR_SWAP_DEVICE_BANNER_TEXT = 'You can remove or swap the OCS device from Logistics only.';

type CaseDeviceTrialBannersProps = {
  caseTrialProgram?: TrialProgram;
  resources: CaseResourceCombined[];
  isCaseDetails?: boolean;
};

export const CaseDeviceTrialBanners = ({
  caseTrialProgram,
  resources,
  isCaseDetails = false,
}: CaseDeviceTrialBannersProps) => {
  const consolesResources = resources.filter((r) => r.resourceType === CaseResourceType.OCSConsole);
  const consoleIds = consolesResources.map((r) => r.resourceId);
  const { data } = useInfiniteOcsConsoleListQuery({ ids: consoleIds.join(',') }, !!consoleIds.length);

  const consolesData = useMemo(() => {
    return data?.pages.map((page) => page.items).flat() || [];
  }, [data]);

  if (!data) return null;

  const devicesTrialSameAsCase = consolesData.every((c) => c.trialProgram?.id === caseTrialProgram?.id);
  const caseHasTrial = !!caseTrialProgram;
  const devicesHaveTrial = consolesData.some((c) => c.trialProgram);
  const isHeartProgram = caseTrialProgram?.organ === OrganType.Heart;
  const showSpecificDeviceBanner = caseHasTrial && !devicesTrialSameAsCase;

  const removeOrSwapDeviceText = isCaseDetails ? ` ${REMOVE_OR_SWAP_DEVICE_BANNER_TEXT}` : '';
  const specificTrialDeviceText = `${SPECIFIC_OCS_DEVICE_TRIAL_BANNER_TEXT}${removeOrSwapDeviceText}`;
  const regularDeviceText = `${OCS_DEVICE_TRIAL_BANNER_TEXT}${removeOrSwapDeviceText}`;

  return (
    <>
      {showSpecificDeviceBanner && !isCaseDetails && (
        <InfoBanner
          title={`${caseTrialProgram?.name} Trial Program`}
          iconType={isHeartProgram ? 'enhanceOrgan' : 'denovoOrgan'}
          iconPosition="center"
        >
          {SPECIFIC_OCS_DEVICE_TRIAL_BANNER_TEXT}
        </InfoBanner>
      )}
      {showSpecificDeviceBanner && isCaseDetails && (
        <InfoBanner iconType="warning">{specificTrialDeviceText}</InfoBanner>
      )}
      {!caseHasTrial && devicesHaveTrial && <InfoBanner iconType="warning">{regularDeviceText}</InfoBanner>}
    </>
  );
};
